/**
 * Feb 2026 — GstLinksPopover
 *
 * Shared internal reference links (Google Drive sheets, contract
 * URLs, etc.) attached to a GST document chain. Links are shared
 * across every doc in the same lineage (Quotation → SO → Invoice
 * → Credit Note) so adding a sheet URL on the Quote surfaces on
 * its SO & INV too.
 *
 * Icon sits next to the comments icon on each row. Clicking opens a
 * dropdown listing existing links with an "Add new link" item.
 */
import { useEffect, useRef, useState } from "react";
import { ExternalLink, Link as LinkIcon, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { apiErrorMessage } from "@/lib/apiError";

export default function GstLinksPopover({ ws, docId, count = 0, onChange }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [label, setLabel] = useState("");
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const { user } = useAuth() || {};
  const labelRef = useRef(null);

  useEffect(() => {
    if (!open || !ws || !docId) return;
    setLoading(true);
    api
      .get(`/gst/workspaces/${ws}/documents/${docId}/links`)
      .then((r) => setItems(r.data || []))
      .catch(() => {
        /* silent */
      })
      .finally(() => setLoading(false));
  }, [open, ws, docId]);

  useEffect(() => {
    if (adding) {
      // Focus the label input when the form opens.
      setTimeout(() => labelRef.current?.focus(), 30);
    }
  }, [adding]);

  const startAdd = () => {
    setLabel("");
    setUrl("");
    setAdding(true);
  };

  const cancelAdd = () => {
    setAdding(false);
    setLabel("");
    setUrl("");
  };

  const save = async () => {
    const lbl = label.trim();
    const u = url.trim();
    if (!lbl || !u) return;
    setBusy(true);
    try {
      const r = await api.post(
        `/gst/workspaces/${ws}/documents/${docId}/links`,
        { label: lbl, url: u },
      );
      setItems((prev) => [...prev, r.data]);
      setAdding(false);
      setLabel("");
      setUrl("");
      if (onChange) onChange((items?.length || 0) + 1);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not add link"));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (lid) => {
    if (!window.confirm("Delete this link?")) return;
    try {
      await api.delete(
        `/gst/workspaces/${ws}/documents/${docId}/links/${lid}`,
      );
      setItems((prev) => prev.filter((l) => l.id !== lid));
      if (onChange) onChange(Math.max(0, (items?.length || 1) - 1));
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not delete link"));
    }
  };

  return (
    <Popover
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) cancelAdd();
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          className="relative inline-flex items-center justify-center w-6 h-6 text-muted-foreground hover:text-primary rounded"
          title={
            count > 0
              ? `${count} shared link${count === 1 ? "" : "s"}`
              : "Add a shared reference link"
          }
          data-testid={`gst-links-btn-${docId}`}
        >
          <LinkIcon className="w-3.5 h-3.5" />
          {count > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 text-[8px] bg-primary text-primary-foreground rounded-full min-w-[14px] h-3.5 px-0.5 flex items-center justify-center font-semibold"
              data-testid={`gst-links-count-${docId}`}
            >
              {count > 9 ? "9+" : count}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="p-3 w-96 z-[1300]"
        data-testid={`gst-links-content-${docId}`}
      >
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
            Shared links
          </div>
          <div className="text-[10px] text-muted-foreground">
            Shared across Quote → SO → Invoice
          </div>
        </div>

        {loading ? (
          <div className="text-xs text-muted-foreground py-2">Loading...</div>
        ) : items.length === 0 ? (
          <div className="text-xs text-muted-foreground py-2">
            No links yet.
          </div>
        ) : (
          <div className="space-y-1.5 max-h-72 overflow-y-auto">
            {items.map((l) => (
              <div
                key={l.id}
                className="group flex items-center gap-2 text-xs border border-border rounded px-2 py-1.5 hover:bg-muted/40"
                data-testid={`gst-link-row-${l.id}`}
              >
                <ExternalLink className="w-3 h-3 text-muted-foreground shrink-0" />
                <a
                  href={l.url}
                  target="_blank"
                  rel="noreferrer"
                  className="font-medium text-primary hover:underline truncate flex-1"
                  title={l.url}
                >
                  {l.label}
                </a>
                {(l.author_id === user?.id || user?.is_super_admin) && (
                  <button
                    type="button"
                    onClick={() => remove(l.id)}
                    className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive shrink-0"
                    title="Delete"
                    data-testid={`gst-link-delete-${l.id}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="mt-2 pt-2 border-t border-border">
          {!adding ? (
            <button
              type="button"
              onClick={startAdd}
              className="w-full flex items-center gap-2 text-xs text-primary hover:bg-primary/5 rounded px-2 py-1.5"
              data-testid={`gst-links-add-${docId}`}
            >
              <Plus className="w-3 h-3" />
              <span>Add new link</span>
            </button>
          ) : (
            <div className="space-y-1.5">
              <div className="space-y-0.5">
                <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Label
                </Label>
                <Input
                  ref={labelRef}
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="e.g. Costing sheet"
                  className="h-7 text-xs rounded-none"
                  maxLength={200}
                  data-testid={`gst-links-label-input-${docId}`}
                />
              </div>
              <div className="space-y-0.5">
                <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  URL
                </Label>
                <Input
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://..."
                  className="h-7 text-xs rounded-none"
                  maxLength={2000}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !busy && label.trim() && url.trim()) {
                      e.preventDefault();
                      save();
                    }
                  }}
                  data-testid={`gst-links-url-input-${docId}`}
                />
              </div>
              <div className="flex justify-end gap-1 pt-0.5">
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 rounded-none text-xs"
                  onClick={cancelAdd}
                  data-testid={`gst-links-cancel-${docId}`}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  className="h-7 rounded-none text-xs"
                  disabled={busy || !label.trim() || !url.trim()}
                  onClick={save}
                  data-testid={`gst-links-save-${docId}`}
                >
                  {busy ? "Saving..." : "Save link"}
                </Button>
              </div>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
